import React from 'react';
import { getCurrencySymbol } from '../../../../shared/constants/billing.js';
import { useAppCharges } from '@/hooks/queries';

export default function AdminChargesSection({ appId, appName, preferredCurrency, showToast }) {
  const { data: charges = [], isLoading: loading } = useAppCharges(appId);

  const getStatusBadge = (status) => {
    const badges = {
      paid: { bg: 'bg-green-100', text: 'text-green-700', label: 'Paid', icon: '✓' },
      unpaid: { bg: 'bg-red-100', text: 'text-red-700', label: 'Unpaid', icon: '⚠' },
      refunded: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Refunded', icon: '↩' },
    };
    const badge = badges[status] || badges.unpaid;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${badge.bg} ${badge.text}`}>
        <span>{badge.icon}</span>
        <span>{badge.label}</span>
      </span>
    );
  };

  const getPaymentMethodBadge = (method) => {
    return (
      <span className="px-2 py-1 rounded text-xs font-medium bg-purple-100 text-purple-700">
        💳 Upfront
      </span>
    );
  };

  const totalPaid = charges.filter(c => c.paymentStatus === 'paid').reduce((sum, c) => sum + parseFloat(c.amount), 0);
  const totalUnpaid = charges.filter(c => c.paymentStatus === 'unpaid').reduce((sum, c) => sum + parseFloat(c.amount), 0);

  return (
    <>
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">💳 Charges</h2>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-3 gap-4 mb-6 pb-6 border-b border-gray-200">
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-600 mb-1">Total Charges</p>
            <p className="text-lg font-bold text-gray-900">{charges.length}</p>
          </div>
          <div className="bg-green-50 rounded-lg p-3">
            <p className="text-xs text-green-600 mb-1">Total Paid</p>
            <p className="text-lg font-bold text-green-700">{getCurrencySymbol(preferredCurrency || 'usd')}{totalPaid.toFixed(2)}</p>
          </div>
          <div className="bg-red-50 rounded-lg p-3">
            <p className="text-xs text-red-600 mb-1">Total Unpaid</p>
            <p className="text-lg font-bold text-red-700">{getCurrencySymbol(preferredCurrency || 'usd')}{totalUnpaid.toFixed(2)}</p>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-8 text-gray-500">Loading charges...</div>
        ) : charges.length === 0 ? (
          <div className="text-center py-8 text-gray-500 text-sm">
            No charges recorded yet. Record a charge to get started.
          </div>
        ) : (
          <div className="space-y-3">
            {charges.map(charge => (
              <div
                key={charge.id}
                className={`p-4 border rounded-lg ${
                  charge.paymentStatus === 'paid'
                    ? 'bg-green-50 border-green-200'
                    : charge.paymentStatus === 'unpaid'
                    ? 'bg-red-50 border-red-200'
                    : 'bg-gray-50 border-gray-200'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900 mb-1">{charge.description}</p>
                    <div className="flex items-center gap-2 flex-wrap">
                      {getStatusBadge(charge.paymentStatus)}
                      {getPaymentMethodBadge(charge.paymentMethod)}
                      {charge.paidAt && (
                        <span className="text-xs text-gray-500">
                          Paid {new Date(charge.paidAt).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-right ml-4">
                    <p className="text-lg font-bold text-gray-900">{getCurrencySymbol(preferredCurrency || 'usd')}{parseFloat(charge.amount).toFixed(2)}</p>
                    <p className="text-xs text-gray-500">
                      {new Date(charge.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

